import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { partial, debounce } from 'underscore';
import moment from 'moment';
import { animateScroll } from 'react-scroll';
import { formatPrice } from 'utils/common';
import i18n from 'utils/translate';
import NavigationButtons from 'components/NavigationButtons';
import RadioButton from 'components/RadioButton';
import Section from 'components/Section';
import TextField from 'components/TextField';
import {
  CALC_API_DEBOUNCE_DURATION,
  DATE_ERROR_MESSAGES,
  PAYMENT_TYPE_ANNUAL,
  PAYMENT_TYPE_BIANNUAL,
  PAYMENT_TYPE_MONTHLY,
  PAYMENT_TYPE_QUARTERLY
} from '../../constants';
import { calculateEndDate, paymentDetails } from '../../utils';
import FilterField from 'components-shared/FilterField';
import DropdownField from 'components/DropdownField';
import RadioButtonGroup from 'components/RadioButtonGroup';
import InfoTextHeader from 'components/InfoTextHeader';
import { toast } from 'react-toastify';
import './styles.scss';
import ErrorMessage from 'components/ErrorMessage';
import { linkTypeShape } from 'utils/types';

const propTypes = {
  calculateQuote: PropTypes.func.isRequired,
  groups: PropTypes.any.isRequired,
  backLink: PropTypes.shape(linkTypeShape),
  nextLink: PropTypes.shape(linkTypeShape).isRequired,
  onChange: PropTypes.func.isRequired
};

const defaultProps = { onChange: console.log };

const associationOptions = ['Wien, Niederösterreich, Burgenland', 'Steiermark, Kärnten'].map(a => {
  return { value: a, label: i18n(a) };
});

class ExpertDetails extends Component {
  constructor(props) {
    super(props);

    this.state = {
      zahlungsweise: PAYMENT_TYPE_ANNUAL,
      versicherungsbeginn: moment().add(1, 'day'),
      infoAccept: false,
      submitted: false,
      validForm: false,
      options: [],
      wrongDate: null,
      showAssocations: false,
      ...props.data
    };
  }

  componentDidMount() {
    this.calcQuote();
    animateScroll.scrollToTop({ duration: 0 });
  }

  handleReverse = e => {
    const {
      backLink: { go } = {
        go: () => {}
      }
    } = this.props;
    e.preventDefault();
    e.stopPropagation();
    go();
  };

  handleSubmit = e => {
    const {
      nextLink: { go } = {
        go: () => {}
      },
      onChange = () => {}
    } = this.props;
    const form = this.refs.form;

    if (form.checkValidity && !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    onChange(this.state);
    go();

    window.dataLayer.push({
      event: 'onlineabschluss',
      formname: 'Sachverständiger- und Dolmetsch-Versicherung',
      action: 'Schritt 1 von 5'
    });
  };

  calcQuote() {
    const { fachgruppen } = this.state;

    if (fachgruppen !== undefined) {
      this.requestQuote();
    }
  }

  requestQuote = debounce(() => {
    const { calculateQuote } = this.props;

    calculateQuote({
      ...this.state
    }).then(quote => {
      this.setState({ quote });
    });
  }, CALC_API_DEBOUNCE_DURATION);

  handleDataChange = (data, key) => {
    const { submitted } = this.state;
    const { onChange } = this.props;

    this.setState(
      {
        [`${key}`]: data
      },
      () => {
        if (key === 'fachgruppen' || key === 'showAssocations') {
          this.calcQuote();
        }
        onChange(this.state);
        if (submitted) {
          this.setState({ validForm: this.refs.form.checkValidity() });
        }
      }
    );
  };

  handleShowAssociationChange = async show => {
    let newValue = show ? associationOptions[0].value : null;
    this.handleDataChange(newValue, 'zustaendigerLandesverband');
    this.handleDataChange(show, 'showAssocations');
  };

  handlePaymentTypeCheck = async (type, e) => {
    if (e.target.checked) {
      await this.setState({ zahlungsweise: type });
    }
  };

  handleInfoAccept = ({ target: { checked: value } }) => {
    const { submitted } = this.state;

    this.setState({ infoAccept: value });
    if (submitted) {
      this.setState({ validForm: this.refs.form.checkValidity() });
    }
  };

  render() {
    const { backLink, groups } = this.props;
    const {
      quote,
      beruf,
      fachgruppen,
      fachgebiete,
      gerichtDerEintragung,
      datumDerEintragung,
      zustaendigerLandesverband,
      versicherungsbeginn,
      zahlungsweise,
      submitted,
      wrongDate,
      downloadedBasicInfo,
      showAssocations,
      infoAccept
    } = this.state;
    const basicInfoDownloadClass = submitted && !downloadedBasicInfo ? 'download-error' : '';
    const basicInfoLink = window.documentLinks ? window.documentLinks.basicInfos : '';

    let price;
    let currency;
    let paymentVariants;

    if (quote) {
      price = quote.price;
      currency = quote.currency;
      paymentVariants = quote.paymentVariants;
    }

    let customError = '';
    let minDate = moment();

    const endDate = calculateEndDate(versicherungsbeginn);

    const sorted = groups.sort((a, b) => (!!a.fachgruppe && a.fachgruppe - b.fachgruppe) || a.klasse - b.klasse);

    const options = sorted.map(o => {
      return {
        value: o.key,
        label: o.fachgruppe ? `${o.fachgruppe} | ${o.beschreibung}` : o.beschreibung,
        customLabel: `Klasse ${o.klasse} | ${
          o.fachgruppe ? `Fachgruppe ${o.fachgruppe} | ${o.beschreibung}` : o.beschreibung
        }`
      };
    });

    if (moment.isMoment(wrongDate) && wrongDate.isValid() && wrongDate.isBefore(minDate)) {
      if (wrongDate.isBefore(moment())) {
        customError = DATE_ERROR_MESSAGES.inFuture;
      }
    }

    return (
      <form
        ref="form"
        onSubmit={this.handleSubmit}
        className={`page-step-wrapper expert-details page-step-wrapper--narrow ${submitted ? 'submitted' : ''}`}
        id="insurance-form"
      >
        <Section header="Information zum Produktvertreiber">
          <InfoTextHeader checked={infoAccept} onChange={value => this.handleInfoAccept(value)} />
        </Section>
        <Section header={i18n('Details zur Tätigkeit')}>
          <div className="row">
            <div className="row">
              <TextField
                label={i18n('Beruf')}
                name="job"
                className="col-xs-12 col-md-6"
                value={beruf}
                required
                onChange={data => this.handleDataChange(data, 'beruf')}
                ref="job"
              />
              <FilterField
                ref="groups"
                label={i18n('Fachgruppe(n)')}
                name="groups"
                className="col-xs-12 col-md-6"
                value={fachgruppen}
                options={options}
                isMulti={true}
                closeMenuOnSelect={false}
                searchFromLeft={false}
                onChange={data =>
                  this.handleDataChange(
                    (data || []).map(item => item.value),
                    'fachgruppen'
                  )
                }
                isFormSubmitted={submitted}
                required
              />
            </div>
            <TextField
              label={i18n('Fachgebiet(e)')}
              name="fields"
              className="col-xs-12 col-md-4"
              value={fachgebiete}
              required
              onChange={data => this.handleDataChange(data, 'fachgebiete')}
              ref="fields"
            />
            <TextField
              label={i18n('Gericht der Eintragung')}
              name="court"
              className="col-xs-12 col-md-4"
              value={gerichtDerEintragung}
              required
              onChange={data => this.handleDataChange(data, 'gerichtDerEintragung')}
              ref="court"
            />
            <TextField
              label={i18n('Datum der Eintragung')}
              name="recordDate"
              className="col-xs-12 col-md-4"
              required
              value={datumDerEintragung}
              date
              placeholder={i18n('TT.MM.JJJJ')}
              onChange={data => this.handleDataChange(data, 'datumDerEintragung')}
              onWrongDate={() => {}}
              ref="recordDate"
            />
          </div>
          <div className="row">
            <div className="col-xs-12">
              <RadioButtonGroup
                required={true}
                label="Mitglied eines Landesverbandes"
                questionMark={i18n(
                  'Für Sachverständige/Dolmetscher, die Verbandsmitglieder sind, reduziert sich die Jahresprämie um € 72,70. Für Mitglieder der Landesverbände Oberösterreich/Salzburg und Tirol/Vorarlberg ist kein Online-Abschluss möglich. Bitte wenden Sie sich in diesem Fall an die Abteilung VH-UHR unter 0316/8037-6228 oder center.gd.vuhr@grawe.at.'
                )}
                id="showAssociations"
                value={showAssocations}
                onChange={data => this.handleShowAssociationChange(data)}
                options={[
                  { label: 'Nein', value: false },
                  { label: 'Ja', value: true }
                ]}
              />
            </div>
          </div>
          {showAssocations && (
            <DropdownField
              name="association"
              className="association-select col-md-6"
              options={associationOptions}
              required
              ref="association"
              value={zustaendigerLandesverband}
              onChange={data => this.handleDataChange(data, 'zustaendigerLandesverband')}
            />
          )}
        </Section>
        <Section header={i18n('Versicherungssumme, Versicherungsdauer und Zahlungsweise')}>
          <div className="sum-wrapper">
            <RadioButton
              className="col-xs-12"
              id="sum"
              text={i18n('Versicherungssumme € 400.000,-')}
              questionMark={i18n(
                'Gemäß §2a SDG beträgt die Mindestversicherungssumme € 400.000,-. Höhere Summen können online nicht abgeschlossen werden. Bitte wenden Sie sich in diesem Fall an die Abteilung VH-UHR unter 0316/8037-6228 oder center.gd.vuhr@grawe.at.'
              )}
              checked={true}
              onChange={() => false}
            />
          </div>
          <div className="row">
            <TextField
              label={i18n('Versicherungsbeginn')}
              name="start_date"
              className="col-xs-12 col-md-6 col-lg-4"
              required
              value={versicherungsbeginn}
              minDate={minDate}
              date
              placeholder={i18n('TT.MM.JJJJ')}
              onChange={data => this.handleDataChange(data, 'versicherungsbeginn')}
              onWrongDate={wrongDate => this.setState({ wrongDate })}
              customError={customError}
              ref="startDate"
            />
            <TextField
              label={i18n('Versicherungsende/Folgefälligkeit')}
              name="end_date"
              className="col-xs-12 col-md-6 col-lg-4"
              value={endDate}
              questionMark={
                'Sollte der Vertrag nicht 3 Monate vor Versicherungsende schriftlich gekündigt werden, verlängert er sich automatisch um ein weiteres Jahr.'
              }
              disabled
            />
          </div>
          <div className="payment-type-wrapper">
            <RadioButton
              className="col-sm-12 col-md-3"
              id="jaehrlich"
              text={i18n('jährlich')}
              checked={zahlungsweise === PAYMENT_TYPE_ANNUAL}
              onChange={partial(this.handlePaymentTypeCheck, PAYMENT_TYPE_ANNUAL)}
            />
            <RadioButton
              className="col-sm-12 col-md-3"
              id="halbjaehrlich"
              text={i18n('halbjährlich')}
              checked={zahlungsweise === PAYMENT_TYPE_BIANNUAL}
              onChange={partial(this.handlePaymentTypeCheck, PAYMENT_TYPE_BIANNUAL)}
            />
            <RadioButton
              className="col-sm-12 col-md-3"
              id="vierteljaehrlich"
              text={i18n('vierteljährlich')}
              checked={zahlungsweise === PAYMENT_TYPE_QUARTERLY}
              onChange={partial(this.handlePaymentTypeCheck, PAYMENT_TYPE_QUARTERLY)}
            />
            <RadioButton
              className="col-sm-12 col-md-3"
              id="monatlich"
              text={i18n('monatlich')}
              checked={zahlungsweise === PAYMENT_TYPE_MONTHLY}
              onChange={partial(this.handlePaymentTypeCheck, PAYMENT_TYPE_MONTHLY)}
            />
          </div>
        </Section>

        <div className="row">
          {versicherungsbeginn && (
            <div className="col-xs-12 hint">
              {i18n(
                `Wir empfehlen Ihnen aufgrund Ihrer Angaben unsere Haftpflichtversicherung für Sachverständige und Dolmetscher mit einer Versicherungsdauer von ${versicherungsbeginn.format(
                  'DD.MM.YYYY'
                )} bis ${endDate}.`
              )}
            </div>
          )}
        </div>

        <div className="row">
          <div className="bib-wrapper col-xs-12">
            <p>
              {i18n(
                'Die standardisierten Produkt- bzw. Basisinformationsblätter beinhalten wichtige Informationen zur Haftpflichtversicherung für Sachverständige und Dolmetscher. Bitte nehmen Sie sich ausreichend Zeit und lesen Sie diese sorgfältig.'
              )}
            </p>
            <a
              className="col-xs-12 basicInfos-pdf"
              href={basicInfoLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => this.handleDataChange(true, 'downloadedBasicInfo')}
            >
              <span className="pdf-icon col-xs-2" />
              <span className={`col-xs-10 ${basicInfoDownloadClass}`}>
                {i18n('Download der standardisierten Produkt- bzw. Basisinformationsblätter')}
                <span className="field-error-message">
                  {i18n(
                    'Die standardisierten Produkt- bzw. Basisinformationsblätter sind verpflichtend herunterzuladen, wenn Sie überlegen, die Haftpflichtversicherung für Sachverständige und Dolmetscher online abzuschließen.'
                  )}
                </span>
              </span>
            </a>
            <p className="p-margin-top">
              {i18n(
                'Sie können dieses Dokument sowie sämtliche vorvertragliche Informationen auch in Papierform erhalten.'
              )}
              <br />
              {i18n('Bitte senden Sie dazu ein Mail mit Ihrer Postanschrift an ')}
              <a href="mailto:center.gd.vuhr@grawe.at?subject=Postversand Vorvertragliche Informationen">
                center.gd.vuhr@grawe.at
              </a>
              .
              <br />
              {i18n('Nach Erhalt der Dokumente können Sie mit dem Online-Vertragsabschluss fortfahren.')}
            </p>
          </div>
        </div>
        <div className="sticky-footer">
          <div className="page-step-wrapper__price">
            {i18n('Prämie (jährlich)')}&nbsp;
            {formatPrice(price, currency)}
            {zahlungsweise !== PAYMENT_TYPE_ANNUAL && !!paymentVariants && paymentDetails(zahlungsweise, quote)}
          </div>
          <NavigationButtons
            nextLink={{
              go: () => {
                this.setState({
                  submitted: true,
                  validForm: this.refs.form.checkValidity && this.refs.form.checkValidity()
                });

                Object.keys(this.refs).forEach(key => {
                  if (this.refs[key].onBlur) {
                    this.refs[key].onBlur();
                  }
                });

                if (
                  quote &&
                  quote.price &&
                  downloadedBasicInfo &&
                  this.refs.form.checkValidity &&
                  this.refs.form.checkValidity()
                ) {
                  toast.dismiss();
                  this.handleSubmit();
                } else {
                  toast.error(<ErrorMessage message={i18n('Bitte überprüfen Sie Ihre Angaben')} />);
                }
              }
            }}
            backLink={backLink}
          />
        </div>
      </form>
    );
  }
}

ExpertDetails.defaultProps = defaultProps;
ExpertDetails.propTypes = propTypes;

export default ExpertDetails;
